import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AssetBrowserPanelComponent from './AssetBrowserPanelComponent';
import {
    setSelectedAsset,
    loadCapabilityAndKPIs,
    setBrowserCategory,
    setSelectedPowertrainId,
    loadPowertrainListForBrowser,
    syncSelectedAssetToPowertrain
} from '../actions';
import {
    getAssetBrowserPanelModel
} from '../selectors';
import { setSearchboxFilter, setAssetTypeFilter, setActiveTab } from '../../ConditionIndicesOverviewV2/actions';
import { allAssetTypes, apiCallStatus } from 'helpers/constants';
import { browserCategories } from '../constants';
import { conditionIndicesViewOptions, toggleButtonOptions } from '../../ConditionIndicesOverviewV2/constants';
import { findFirstMatchingAssetInHierarchy, findAssetByIdInHierarchy } from '../../ConditionIndicesOverviewV2/HierarchyView/hierarchyHelpers';
import {
    filterAssetsBySearchAndType,
    getAssetTypeValue,
    getNormalizedAssetId,
    isSameAsset,
    normalizeCrossAssetV2Asset
} from '../helpers/assetIdentity';

const AssetBrowserPanel = ({ conditionIndicesView, model }) => {
    const dispatch = useDispatch();
    const {
        selectedAsset,
        selectedOrganization,
        selectedCategory,
        selectedPowertrainId,
        powertrainList,
        powertrainListLoadingStatus,
        powertrainFilter,
        assetListFilter,
        hierarchyFilter,
        hierarchyData,
        hierarchyLoadingStatus,
        assetOverviewData,
        assetOverviewLoadingStatus,
        activeTab,
        generalFilterAssets,
        selectedSites
    } = model;
    const lastAutoSelectedSearchRef = useRef(null);
    const location = useLocation();
    // Snapshot the deep-linked assetId once on mount: the global GeneralFilter header also reads
    // ?assetId= (for its own cross-org asset-search flow) and rewrites/strips it from the URL
    // shortly after load, which would otherwise repeatedly restart this effect and re-trigger
    // the whole find-and-select flow (extra reloads, flicker, duplicate API calls).
    const [urlAssetId] = useState(() => new URLSearchParams(location?.search || '').get('assetId'));

    // Blue-dot: on-graph signal count per asset id, from the shared chartView bucket (same source V1 uses).
    const chartData = useSelector(({ chartView }) => chartView?.chartData);
    const selectedAssetSignalCounts = useMemo(() => {
        const counts = {};
        (chartData || []).forEach((item) => {
            const assetOrComponent = item?.asset || item?.component;
            const id = getNormalizedAssetId(assetOrComponent);
            if (!id) { return; }
            counts[id] = (counts[id] || 0) + 1;
        });
        return counts;
    }, [chartData]);
    const selectedAssetIds = useMemo(() => Object.keys(selectedAssetSignalCounts), [selectedAssetSignalCounts]);

    const isListView = conditionIndicesView === conditionIndicesViewOptions.LIST;
    const isPowertrainCategory = isListView && selectedCategory === browserCategories.POWERTRAINS;
    const targetConditionOverviewTab = isPowertrainCategory
        ? toggleButtonOptions.POWERTRAINS
        : toggleButtonOptions.ASSETS;

    useEffect(() => {
        if (activeTab !== targetConditionOverviewTab) {
            dispatch(setActiveTab(targetConditionOverviewTab));
        }
    }, [activeTab, dispatch, targetConditionOverviewTab]);


    useEffect(() => {
        if (!urlAssetId || selectedAsset) { return; }
        if (selectedCategory !== browserCategories.ASSETS) {
            dispatch(setBrowserCategory(browserCategories.ASSETS));
        }
    }, [urlAssetId, selectedAsset, selectedCategory, dispatch]);

    useEffect(() => {
        const organizationId = selectedOrganization?.organizationID;
        if (!isListView || !organizationId) {
            return;
        }
        dispatch(loadPowertrainListForBrowser(organizationId));
    }, [dispatch, isListView, selectedOrganization?.organizationID, JSON.stringify(selectedSites)]);

    // When in Powertrains list view, keep the selected powertrain in sync with the selected asset.
    // Covers: switching from hierarchy to list view, and clicking a comparison set signal.
    useEffect(() => {
        if (!isPowertrainCategory || !selectedAsset) { return; }
        dispatch(syncSelectedAssetToPowertrain(selectedAsset));
    }, [dispatch, isPowertrainCategory, selectedAsset]);

    const hierarchyAssetTypeOptions = useMemo(() => {
        const collected = new Set([allAssetTypes]);

        (generalFilterAssets || []).forEach((asset) => {
            const type = getAssetTypeValue(asset);
            if (type) {
                collected.add(type);
            }
        });

        const walk = (node) => {
            if (!node) { return; }
            if (Array.isArray(node)) {
                node.forEach(walk);
                return;
            }

            const type = node?.assetType || node?.componentTypeName || node?.assetTypeName;
            if (type) { collected.add(type); }

            Object.keys(node).forEach((key) => {
                if (Array.isArray(node[key])) {
                    walk(node[key]);
                }
            });
        };

        walk(hierarchyData);
        return Array.from(collected);
    }, [generalFilterAssets, hierarchyData]);

    // No default powertrain preselection (matches V1) — only resolve once the user (or search) picks one.
    const selectedPowertrain = useMemo(() => {
        if (!powertrainList?.length || !selectedPowertrainId) { return null; }
        return powertrainList.find((item) => {
            const resolvedId = item?.powertrainId ?? item?.systemId ?? item?.id;
            return resolvedId?.toString() === selectedPowertrainId?.toString();
        }) || null;
    }, [powertrainList, selectedPowertrainId]);

    const selectedPowertrainAssets = useMemo(() => {
        if (!selectedPowertrain) { return []; }
        return selectedPowertrain?.assets || [];
    }, [selectedPowertrain]);

    const powertrainAssetTypeOptions = useMemo(() => {
        const collected = new Set([allAssetTypes]);
        selectedPowertrainAssets.forEach((asset) => {
            const type = getAssetTypeValue(asset);
            if (type) {
                collected.add(type);
            }
        });
        return Array.from(collected);
    }, [selectedPowertrainAssets]);

    const listCategoryFilter = isPowertrainCategory ? powertrainFilter : assetListFilter;
    const appliedFilter = isListView ? listCategoryFilter : hierarchyFilter;
    const searchText = appliedFilter?.searchBox || '';
    const selectedAssetTypeFilter = appliedFilter?.assetTypeFilter || allAssetTypes;

    const filteredPowertrainAssets = useMemo(() =>
        filterAssetsBySearchAndType(selectedPowertrainAssets, searchText, selectedAssetTypeFilter),
        [searchText, selectedAssetTypeFilter, selectedPowertrainAssets]);

    // All assets across every powertrain — lets search find a match outside the currently browsed powertrain.
    const allPowertrainAssetsFlat = useMemo(() =>
        (powertrainList || []).flatMap((powertrain) => powertrain?.assets || []),
        [powertrainList]);

    // Search should not be limited to the currently selected powertrain: if a match is found in a
    // different powertrain, switch to it so its details open (mirrors clicking an asset directly).
    useEffect(() => {
        if (!isPowertrainCategory || !searchText?.trim()) { return; }
        const matchedAsset = allPowertrainAssetsFlat.find((asset) =>
            filterAssetsBySearchAndType([asset], searchText, selectedAssetTypeFilter).length > 0
        );
        if (matchedAsset) {
            dispatch(syncSelectedAssetToPowertrain(matchedAsset));
        }
    }, [isPowertrainCategory, searchText, selectedAssetTypeFilter, allPowertrainAssetsFlat, dispatch]);

    const powertrainOptions = useMemo(() => {
        return (powertrainList || []).map((powertrain) => {
            const id = powertrain?.powertrainId ?? powertrain?.systemId ?? powertrain?.id;
            const label = powertrain?.powertrainName || powertrain?.name || powertrain?.systemName || id;
            return { id, label };
        });
    }, [powertrainList]);

    const assetTypeOptions = useMemo(() => {
        const baseOptions = isPowertrainCategory ? powertrainAssetTypeOptions : hierarchyAssetTypeOptions;
        if (!selectedAssetTypeFilter || baseOptions.includes(selectedAssetTypeFilter)) {
            return baseOptions;
        }
        return [...baseOptions, selectedAssetTypeFilter];
    }, [hierarchyAssetTypeOptions, isPowertrainCategory, powertrainAssetTypeOptions, selectedAssetTypeFilter]);
    const effectiveSelectedAsset = selectedAsset;

    const handleSelectAsset = useCallback((asset) => {
        const normalizedAsset = normalizeCrossAssetV2Asset(asset);
        // Skip redundant reselect/reload when the asset is already the one selected (e.g. an
        // unrelated generalFilter reset re-runs the auto-select effects for the same target).
        if (normalizedAsset && isSameAsset(normalizedAsset, selectedAsset)) { return; }
        dispatch(setSelectedAsset(normalizedAsset));
        if (normalizedAsset) {
            dispatch(loadCapabilityAndKPIs(normalizedAsset));
        }
    }, [dispatch, selectedAsset]);

    // Auto-select the first search match in Hierarchy/Assets-list browsing so the tree auto-expands
    // and highlights it, same as the Condition Overview hierarchy view.
    useEffect(() => {
        if (isPowertrainCategory) { return; }
        const searchTerm = searchText?.trim();
        if (!searchTerm) {
            lastAutoSelectedSearchRef.current = null;
            return;
        }
        const viewLoadingStatus = isListView ? assetOverviewLoadingStatus : hierarchyLoadingStatus;
        if (viewLoadingStatus !== apiCallStatus.SUCCESS) { return; }
        if (lastAutoSelectedSearchRef.current === searchTerm) { return; }
        const sourceData = isListView ? assetOverviewData : hierarchyData;
        const matchingAsset = findFirstMatchingAssetInHierarchy(sourceData, searchTerm);
        if (matchingAsset) {
            lastAutoSelectedSearchRef.current = searchTerm;
            handleSelectAsset(matchingAsset);
        }
    }, [
        isPowertrainCategory,
        isListView,
        searchText,
        hierarchyLoadingStatus,
        assetOverviewLoadingStatus,
        hierarchyData,
        assetOverviewData,
        handleSelectAsset
    ]);

    useEffect(() => {
        if (isPowertrainCategory || !urlAssetId || selectedAsset) { return; }
        const viewLoadingStatus = isListView ? assetOverviewLoadingStatus : hierarchyLoadingStatus;
        if (viewLoadingStatus !== apiCallStatus.SUCCESS) { return; }
        const sourceData = isListView ? assetOverviewData : hierarchyData;
        const matchingAsset = findAssetByIdInHierarchy(sourceData, urlAssetId);
        if (matchingAsset) {
            handleSelectAsset(matchingAsset);
        }
    }, [
        isPowertrainCategory,
        urlAssetId,
        selectedAsset,
        isListView,
        hierarchyLoadingStatus,
        assetOverviewLoadingStatus,
        hierarchyData,
        assetOverviewData,
        handleSelectAsset
    ]);

    const handleSearchChange = useCallback((value) => {
        if (activeTab !== targetConditionOverviewTab) {
            dispatch(setActiveTab(targetConditionOverviewTab));
        }
        dispatch(setSearchboxFilter(value));
    }, [activeTab, dispatch, targetConditionOverviewTab]);

    const handleAssetTypeChange = useCallback((value) => {
        if (activeTab !== targetConditionOverviewTab) {
            dispatch(setActiveTab(targetConditionOverviewTab));
        }
        dispatch(setAssetTypeFilter(value));
    }, [activeTab, dispatch, targetConditionOverviewTab]);

    const handleCategoryChange = useCallback((category) => {
        dispatch(setBrowserCategory(category));
        dispatch(setActiveTab(category));
    }, [dispatch]);

    const handlePowertrainChange = useCallback((powertrainId) => {
        dispatch(setSelectedPowertrainId(powertrainId));
    }, [dispatch]);

    return (
        <AssetBrowserPanelComponent
            selectedAsset={effectiveSelectedAsset}
            selectedOrganization={selectedOrganization}
            conditionIndicesView={conditionIndicesView}
            onSelectAsset={handleSelectAsset}
            searchText={searchText}
            assetTypeFilter={selectedAssetTypeFilter}
            assetTypeOptions={assetTypeOptions}
            onSearchChange={handleSearchChange}
            onAssetTypeChange={handleAssetTypeChange}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            powertrainOptions={powertrainOptions}
            selectedPowertrainId={selectedPowertrain?.powertrainId ?? selectedPowertrain?.systemId ?? selectedPowertrain?.id}
            onPowertrainChange={handlePowertrainChange}
            powertrainAssets={filteredPowertrainAssets}
            isPowertrainLoading={isPowertrainCategory && (
                powertrainListLoadingStatus === apiCallStatus.LOADING ||
                powertrainListLoadingStatus === null
            )}
            selectedAssetIds={selectedAssetIds}
            selectedAssetSignalCounts={selectedAssetSignalCounts}
        />
    );
};

AssetBrowserPanel.propTypes = {
    conditionIndicesView: PropTypes.string,
    model: PropTypes.shape({
        selectedAsset: PropTypes.object,
        selectedOrganization: PropTypes.shape({
            organizationID: PropTypes.number
        }),
        selectedCategory: PropTypes.string,
        selectedPowertrainId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        powertrainList: PropTypes.array,
        powertrainListLoadingStatus: PropTypes.string,
        powertrainFilter: PropTypes.object,
        assetListFilter: PropTypes.object,
        hierarchyFilter: PropTypes.object,
        hierarchyData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        hierarchyLoadingStatus: PropTypes.string,
        assetOverviewData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        assetOverviewLoadingStatus: PropTypes.string,
        activeTab: PropTypes.string,
        generalFilterAssets: PropTypes.array,
        selectedSites: PropTypes.array
    })
};

const mapStateToProps = (state) => ({
    model: getAssetBrowserPanelModel(state)
});

export default connect(mapStateToProps)(AssetBrowserPanel);
